import { ref } from "vue";

export enum ComparisonOperator {
	Less = "<",
	LessOrEqual = "<=",
	More = ">",
	MoreOrEqual = ">=",
	Equal = "=",
	NotEqual = "<>",
}

export const NUMBER_COMPARISON_OPERATORS: ComparisonOperator[] = [
	ComparisonOperator.Less,
	ComparisonOperator.LessOrEqual,
	ComparisonOperator.More,
	ComparisonOperator.MoreOrEqual,
	ComparisonOperator.Equal,
	ComparisonOperator.NotEqual,
];

export const STRING_COMPARISON_OPERATORS: ComparisonOperator[] = [
	ComparisonOperator.Equal,
	ComparisonOperator.NotEqual,
];

export type ComparableType = string | number | boolean;

export type ArrayFilter = {
	key: string;
	operator: ComparisonOperator;
	compareTo: string;
	getter: (obj: any) => ComparableType;
	enabled: boolean;
};

export const useArrayFilters = () => {
	const filters = ref<ArrayFilter[]>([]);

	const addFilter = (filter: ArrayFilter) => {
		filters.value.push(filter);
	};

	const deleteFilter = (index: number) => {
		filters.value.splice(index, 1);
	};

	const setFilterEnabled = (index: number, enabled: boolean) => {
		if (index >= filters.value.length) {
			throw new Error(
				`Index ${index} is out of bounds of an array of length ${filters.value.length}`
			);
		}
		filters.value[index].enabled = enabled;
	};

	const applyFilters = (array: any[], filters: ArrayFilter[]) =>
		array.filter(item =>
			filters
				.filter(f => f.enabled)
				.every(filter =>
					compareValues(
						filter.getter(item),
						filter.compareTo,
						filter.operator
					)
				)
		);

	return {
		filters,
		addFilter,
		deleteFilter,
		setFilterEnabled,
		applyFilters,
	};
};

export const compareValues = <T extends ComparableType>(
	value: T,
	compareTo: string,
	operator: ComparisonOperator
) => {
	let other: ComparableType = compareTo;
	if (typeof value === "number") {
		other = Number(compareTo);
	} else if (typeof value === "boolean") {
		other = compareTo === "true";
	}

	switch (operator) {
		case ComparisonOperator.Less:
			return value < other;
		case ComparisonOperator.LessOrEqual:
			return value <= other;
		case ComparisonOperator.More:
			return value > other;
		case ComparisonOperator.MoreOrEqual:
			return value >= other;
		case ComparisonOperator.Equal:
			return value === other;
		case ComparisonOperator.NotEqual:
			return value !== other;
	}
};

export function saveFilters(filters: ArrayFilter[], key: string) {
	localStorage.setItem(key, JSON.stringify(filters));
}

export function restoreFilters(key: string): Omit<ArrayFilter, "getter">[] {
	const filters = localStorage.getItem(key);
	return filters ? JSON.parse(filters) : [];
}
